/**
 * useNotifications
 *
 * Monitors portfolio state every 60 s and fires Web Notifications via the
 * service-worker registration (registration.showNotification).  This approach
 * works for an installed PWA on iOS 16.4 + and any modern desktop browser
 * without requiring a VAPID / push server.
 *
 * Alert types
 * ─────────────────────────────────────────────────────────────────────────────
 * red        – Position cushion drops below 5 %
 * amber      – Position cushion in 5–15 % zone
 * cryptoPct  – Crypto allocation exceeds 15 % of NLV
 * leverage   – TQQQ+UPRO vs SMIA+SCHD ratio outside 75:25 – 85:15
 * weekly     – Sunday 9 am portfolio review reminder
 *
 * Transition tracking
 * ─────────────────────────────────────────────────────────────────────────────
 * Each alert only fires ONCE when a condition first becomes true.  It is
 * cleared (and will re-fire if the condition returns) when the condition
 * resolves.  State is persisted in localStorage so it survives page reloads.
 */

import { useState, useEffect, useRef } from 'react';
import { loadPositions, calcCushion, getStatus } from './storage';

/* ── localStorage keys ── */
const PREFS_KEY = 'isa_notif_prefs';
const STATE_KEY = 'isa_notif_state';
const NLV_KEY   = 'isa_nlv';

/* ── Default preference values ── */
export const DEFAULT_PREFS = {
  enabled:    false,  // master on/off (require explicit opt-in)
  red:        true,
  amber:      true,
  cryptoPct:  true,
  leverage:   true,
  weekly:     true,
};

/* ── Helpers ── */
function loadPrefs() {
  try { return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(PREFS_KEY) ?? '{}') }; }
  catch { return { ...DEFAULT_PREFS }; }
}
function savePrefs(p) { try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch {} }

function loadAlertState() {
  const defaults = { redIds: [], amberIds: [], cryptoFired: false, leverageFired: false, weeklyDate: '' };
  try { return { ...defaults, ...JSON.parse(localStorage.getItem(STATE_KEY) ?? '{}') }; }
  catch { return defaults; }
}
function saveAlertState(s) { try { localStorage.setItem(STATE_KEY, JSON.stringify(s)); } catch {} }

/** Show a notification via the service-worker registration. */
async function dispatchNotification(title, body, tag) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    await reg.showNotification(title, {
      body,
      icon:  '/icon-192.png',
      badge: '/icon-192.png',
      tag:   tag ?? title,          // deduplicates identical alerts
      requireInteraction: false,
    });
  } catch { /* silent — browser may block in certain contexts */ }
}

/** Evaluate all conditions and fire notifications as needed. */
function evaluate() {
  const prefs = loadPrefs();
  if (!prefs.enabled) return;

  const positions = loadPositions();
  const nlv       = Number(localStorage.getItem(NLV_KEY)) || 0;
  const st        = loadAlertState();

  /* ── 1. RED / AMBER cushion alerts ── */
  const nowRed   = [];
  const nowAmber = [];

  for (const p of positions) {
    const cushion = calcCushion(Number(p.currentPrice), Number(p.stopPrice));
    const status  = getStatus(cushion);

    if (status === 'RED') {
      nowRed.push(p.id);
      if (prefs.red && !st.redIds.includes(p.id)) {
        dispatchNotification(
          `🔴 ${p.name} — Stop Risk`,
          `Cushion at ${cushion.toFixed(1)}% — below 5% danger threshold`,
          `red-${p.id}`,
        );
      }
    }

    if (status === 'AMBER') {
      nowAmber.push(p.id);
      if (prefs.amber && !st.amberIds.includes(p.id)) {
        dispatchNotification(
          `🟡 ${p.name} Alert — Cushion at ${cushion.toFixed(1)}%`,
          `Approaching stop loss — in caution zone (5–15%)`,
          `amber-${p.id}`,
        );
      }
    }
  }

  // Update tracked IDs — only IDs still in that status stay tracked
  st.redIds   = nowRed;
  st.amberIds = nowAmber;

  /* ── 2. Crypto allocation > 15 % of NLV ── */
  const cryptoVal = positions
    .filter(p => p.assetClass === 'Crypto')
    .reduce((s, p) => s + Number(p.shares) * Number(p.currentPrice), 0);
  const cryptoPct = nlv > 0 ? (cryptoVal / nlv) * 100 : 0;

  if (cryptoPct > 15) {
    if (prefs.cryptoPct && !st.cryptoFired) {
      dispatchNotification(
        `₿ Crypto Overweight — ${cryptoPct.toFixed(1)}% of NLV`,
        'Target is <15% — consider trimming or rebalancing',
        'crypto-overweight',
      );
      st.cryptoFired = true;
    }
  } else {
    st.cryptoFired = false;
  }

  /* ── 3. Leverage ratio outside 75:25 – 85:15 ── */
  const findPos = name => positions.find(p => p.name.toUpperCase() === name) ?? null;
  const posVal  = pos  => pos ? Number(pos.shares) * Number(pos.currentPrice) : 0;
  const levVal  = posVal(findPos('TQQQ')) + posVal(findPos('UPRO'));
  const defVal  = posVal(findPos('SMIA')) + posVal(findPos('SCHD'));
  const total   = levVal + defVal;
  const levPct  = total > 0 ? (levVal / total) * 100 : 0;
  const inRange = total > 0 && levPct >= 75 && levPct <= 85;

  if (!inRange && total > 0) {
    if (prefs.leverage && !st.leverageFired) {
      const defPct = 100 - levPct;
      dispatchNotification(
        `⚖️ Leverage Off-Target — ${levPct.toFixed(1)}:${defPct.toFixed(1)}`,
        'Outside 75:25 – 85:15 range — rebalancing needed',
        'leverage-imbalance',
      );
      st.leverageFired = true;
    }
  } else {
    st.leverageFired = false;
  }

  /* ── 4. Sunday 9 am weekly review reminder ── */
  if (prefs.weekly) {
    const now  = new Date();
    const hour = now.getHours();
    if (now.getDay() === 0 && hour >= 9 && hour < 10) {
      const today = now.toISOString().slice(0, 10);
      if (st.weeklyDate !== today) {
        dispatchNotification(
          '📋 Weekly Review — Sunday Check-In',
          'Time to review positions, trail stops, and your weekly plan',
          'weekly-review',
        );
        st.weeklyDate = today;
      }
    }
  }

  saveAlertState(st);
}

/* ── Hook ── */

export function useNotifications() {
  const [permission, setPermission] = useState(() =>
    'Notification' in window ? Notification.permission : 'unsupported'
  );
  const [prefs, setPrefsState] = useState(loadPrefs);
  const intervalRef = useRef(null);

  /* Save prefs and update React state atomically */
  function setPrefs(next) {
    setPrefsState(next);
    savePrefs(next);
  }

  /* Request browser permission — must be called from a user gesture */
  async function requestPermission() {
    if (!('Notification' in window)) return 'unsupported';
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }

  /* Re-start evaluation loop whenever prefs.enabled or permission changes */
  useEffect(() => {
    clearInterval(intervalRef.current);

    // Re-read permission directly in case it changed outside React
    const live = 'Notification' in window ? Notification.permission : 'unsupported';
    if (live !== permission) setPermission(live);

    if (!loadPrefs().enabled || live !== 'granted') return;

    evaluate();                                           // immediate first run
    intervalRef.current = setInterval(evaluate, 60_000); // then every 60 s

    return () => clearInterval(intervalRef.current);
  }, [prefs.enabled, permission]); // eslint-disable-line react-hooks/exhaustive-deps

  return { permission, prefs, setPrefs, requestPermission };
}
